import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  AlertCircleIcon,
  FileUploadIcon
} from "@hugeicons-pro/core-solid-rounded";
import {
  LinkSquare02Icon,
  UnavailableIcon
} from "@hugeicons-pro/core-stroke-rounded";
import moment from "moment";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx, formatToCurrency } from "shared/utils";
import { FraudAlertStatusBadge } from "shared/app/FraudAlertCard";
import {
  fraudAlertCityState,
  fraudAlertReviewUrl,
  fraudAlertTitle,
  openFraudAlertReview,
  safeExternalUrl,
  shortListingUrl
} from "shared/app/fraudAlertUtils";
import type { FraudAlert, FraudAlertStatus } from "api/propertyShield";

const ROW_STYLE: Record<
  FraudAlertStatus,
  { row: string; text: string; icon: IconSvgElement; iconColor: string }
> = {
  pending_confirmation: {
    row: "bg-primary-20",
    text: "text-primary-700",
    icon: AlertCircleIcon,
    iconColor: "text-primary-500"
  },
  active: {
    row: "bg-warning-50",
    text: "text-warning-700",
    icon: FileUploadIcon,
    iconColor: "text-warning-500"
  },
  false_red_flag: {
    row: "bg-alpha-40",
    text: "text-content-200",
    icon: UnavailableIcon,
    iconColor: "text-content-200"
  },
  removed: {
    row: "bg-alpha-40",
    text: "text-content-200",
    icon: UnavailableIcon,
    iconColor: "text-content-200"
  }
};

const subline = (alert: FraudAlert, showUnit: boolean) =>
  [
    showUnit ? alert.unit_name : null,
    alert.listed_for ? formatToCurrency(alert.listed_for) : null,
    fraudAlertCityState(alert.location),
    moment(alert.identified_at).format("MMM D")
  ]
    .filter(Boolean)
    .join(" • ");

/** One alert, colored by status; shared by the property section and the unit tab. */
export const FraudAlertRow = ({
  alert,
  showUnit = false
}: {
  alert: FraudAlert;
  showUnit?: boolean;
}) => {
  const style = ROW_STYLE[alert.status];
  const listingUrl = safeExternalUrl(alert.listing_url);

  return (
    <li
      className={cx(
        "flex flex-wrap items-center gap-3 rounded-lg px-5 py-4",
        style.row
      )}
    >
      <HugeiconsIcon
        icon={style.icon}
        size={20}
        className={cx("shrink-0 self-start mt-0.5", style.iconColor)}
      />
      <div className={cx("min-w-0 flex-1 text-sm", style.text)}>
        <p className="font-bold break-words">
          {fraudAlertTitle(alert.listing_platform)}
        </p>
        <p className="font-medium">{subline(alert, showUnit)}</p>
        {listingUrl ? (
          <a
            href={listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex max-w-full items-center gap-1 text-xs text-content-400 hover:text-content-700"
          >
            <span className="truncate">{shortListingUrl(listingUrl)}</span>
            <HugeiconsIcon
              icon={LinkSquare02Icon}
              size={12}
              className="shrink-0"
            />
          </a>
        ) : null}
      </div>
      <FraudAlertStatusBadge status={alert.status} />
      {fraudAlertReviewUrl(alert) ? (
        <Button
          type="button"
          size={ButtonSize.XS}
          variant={ButtonVariant.BASE}
          HugeIcon={LinkSquare02Icon}
          iconPosition="right"
          onClick={() => openFraudAlertReview(alert)}
        >
          Open
        </Button>
      ) : null}
    </li>
  );
};
